/**
 * SecurityScreen — "Il mio livello di sicurezza" (manuale INTERATTIVO).
 *
 * Diverso dall'InfoScreen (riferimento statico + GDPR): qui l'utente vede la
 * PROPRIA situazione attuale, il proprio livello di protezione, e per ogni
 * opzione disponibile una valutazione ONESTA — quando è utile e quando NON lo
 * è / cosa costa — così può decidere da solo se e quando passare a Premium.
 *
 * Niente paternalismo: la base (E2E, effimero, zero dati personali) è sempre
 * attiva e gratis. Le opzioni avanzate sono RINFORZI SITUAZIONALI, non un
 * "ti manca qualcosa".
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Frame, Header, Footer, Btn, Box } from '../components/Frame';
import { Section } from '../components/Section';
import { useStore } from '../lib/store';
import { hasLocalBackup, loadLastSigil } from '../lib/pinCrypto';
import { areNotificationsEnabled } from '../lib/notifications';
import { isSelfReadable, setSelfReadable } from '../lib/messaging';
import { isNukeEnabled, setNukeEnabled } from '../lib/nuke';
import { getMyAccount } from '../lib/derivedSigil';

/** Card di una singola opzione: stato + cosa fa + utile/non utile + azione. */
function Opt({ title, state, stateColor = 'text-ink-dim', what, useful, cost, children }) {
  return (
    <div className="border border-ink-dim/50 rounded-xl p-4 mb-3 space-y-1 bg-ink/[0.015]">
      <div className="flex justify-between items-start gap-2">
        <span className="text-ink text-[15px] text-glow">{title}</span>
        <span className={`text-[12px] text-right shrink-0 ${stateColor}`}>{state}</span>
      </div>
      {what && <p className="text-[13px] text-ink-dim">{what}</p>}
      {useful && <p className="text-[13px]"><span className="text-ink">✓ utile se</span> <span className="text-ink-dim">{useful}</span></p>}
      {cost && <p className="text-[13px]"><span className="text-amber">✕ attenzione</span> <span className="text-ink-dim">{cost}</span></p>}
      {children}
    </div>
  );
}

export default function SecurityScreen() {
  const navigate = useNavigate();
  const { sigil, premiumStatus, premiumExpiresAt, premiumDaysLeft, premiumGraceDaysLeft } = useStore();

  const [nuke, setNuke] = useState(() => { try { return isNukeEnabled(); } catch { return false; } });
  const [selfRead, setSelfRead] = useState(() => { try { return isSelfReadable(); } catch { return true; } });
  const notifOn = (() => { try { return areNotificationsEnabled(); } catch { return false; } })();
  const accountOnDevice = (() => { try { return hasLocalBackup() || !!loadLastSigil(); } catch { return false; } })();

  const [idMode, setIdMode] = useState('open');
  const [masterFindable, setMasterFindable] = useState(true);
  const [derivedCount, setDerivedCount] = useState(0);
  // Stato premium fresco (l'utente può arrivare qui in deep-link senza passare dall'inbox).
  const [pStatus, setPStatus] = useState(premiumStatus || 'none');
  const [pExpires, setPExpires] = useState(premiumExpiresAt ?? null);
  const [pDays, setPDays] = useState(premiumDaysLeft ?? null);
  const [pGrace, setPGrace] = useState(premiumGraceDaysLeft ?? null);

  useEffect(() => {
    let off = false;
    (async () => {
      try {
        const r = await getMyAccount();
        if (off) return;
        setIdMode(r.identityMode || 'open');
        setMasterFindable(r.masterFindable !== false);
        setDerivedCount((r.derivedSigils || []).length);
        setPStatus(r.premiumStatus || 'none');
        setPExpires(r.premiumExpiresAt ?? null);
        setPDays(r.premiumDaysLeft ?? null);
        setPGrace(r.premiumGraceDaysLeft ?? null);
      } catch { /* non bloccante: i toggle locali funzionano comunque */ }
    })();
    return () => { off = true; };
  }, []);

  const premiumActive = pStatus === 'active' || pStatus === 'lifetime' || pStatus === 'grace';

  function toggleNuke() {
    if (!nuke) {
      if (!confirm(
        'Attivare Nuke 5-PIN?\n\n'
        + 'Dopo 5 PIN sbagliati di fila, Nullum cancella tutto su questo telefono: '
        + 'chiavi, sigil, backup. Torna come appena installata.\n\n'
        + 'Serve se qualcuno ti costringe a sbloccare il telefono: provando PIN a caso '
        + 'distrugge l\'accesso. Tu recuperi con le 24 parole.\n\n'
        + 'Attenzione: se sbagli tu il PIN 5 volte perdi l\'accesso da qui '
        + '(lo recuperi solo con le 24 parole). Tienile al sicuro prima di attivare.'
      )) return;
    }
    const v = !nuke;
    try { setNukeEnabled(v); } catch {}
    setNuke(v);
  }

  function toggleSelfRead() {
    const v = !selfRead;
    try { setSelfReadable(v); } catch {}
    setSelfRead(v);
  }

  // Rinforzi SITUAZIONALI (non difetti se spenti). Per il "livello".
  const hardening = [
    { on: nuke, label: 'Nuke' },
    { on: idMode === 'reserved' || !masterFindable, label: 'Riservata' },
    { on: !selfRead, label: 'Deniability' },
    { on: !notifOn, label: 'No-token' },
  ];
  const activeCount = hardening.filter((h) => h.on).length;

  return (
    <Frame statusLeft="LA MIA SICUREZZA" statusRight="5G">
      <Header title="// La mia sicurezza" crumb="◀ indietro" onCrumbClick={() => navigate('/inbox')} />

      <p className="text-[14px] text-ink-dim mb-3">
        Questa è la <b className="text-ink">tua</b> situazione, @{sigil || '???'}.
        La protezione di base è sempre attiva e gratis. Qui decidi se ti servono
        i rinforzi in più.
      </p>

      {/* ── PROTEZIONE DI BASE (sempre on) ───────────────────────── */}
      <Box variant="success" className="text-[14px] mb-3 space-y-1">
        <p className="text-ink">🛡 Protezione di base (sempre attiva, gratis)</p>
        <ul className="list-disc pl-5 text-[13px] text-ink-dim space-y-1">
          <li>Solo tu e chi ti scrive potete leggere i messaggi. Il server vede solo dati illeggibili.</li>
          <li>I messaggi spariscono dopo la lettura (scegli tu quando).</li>
          <li>Niente nome, email, telefono o rubrica.</li>
          <li>Le tue 24 parole non restano sul telefono. I tentativi di PIN sono limitati.</li>
          <li>Un codice per ogni chat ti dice che nessuno si è messo in mezzo.</li>
        </ul>
      </Box>

      {/* ── 3 LIVELLI (la scelta semplice) ───────────────────────── */}
      <p className="text-[15px] text-ink mt-4 mb-1">Vuoi di più? Scegli un livello.</p>
      <p className="text-[12px] text-ink-dim mb-2">
        Restare su Standard va benissimo: la base basta per quasi tutti.
      </p>

      <div className="border border-ink rounded-xl p-3 mb-2 bg-ink/[0.04]">
        <div className="flex justify-between items-center">
          <span className="text-ink text-glow text-[15px]">✓ Standard</span>
          <span className="text-[12px] text-ink-dim">sei qui · gratis</span>
        </div>
        <p className="text-[13px] text-ink-dim mt-1">La protezione di base qui sopra. Non devi fare nulla.</p>
      </div>

      <div className="border border-ink-dim/60 rounded-xl p-3 mb-2">
        <div className="flex justify-between items-center">
          <span className="text-ink text-[15px]">🎭 Riservato</span>
          <span className="text-[12px] text-ink-dim">{(idMode === 'reserved' || !masterFindable) ? 'attivo' : 'disponibile'}</span>
        </div>
        <p className="text-[13px] text-ink-dim mt-1">Sparisci dalla ricerca: ti raggiungono solo via invito.</p>
        <button onClick={() => navigate('/premium')} className="mt-2 border border-ink-dim text-ink hover:border-ink rounded-lg px-3 py-2 text-[13px]">Gestisci →</button>
      </div>

      <div className="border border-amber rounded-xl p-3 mb-4">
        <div className="flex justify-between items-center">
          <span className="text-amber text-[15px]">⚡ Massima sicurezza</span>
          <span className="text-[12px] text-amber">Premium</span>
        </div>
        <p className="text-[13px] text-ink-dim mt-1">Nascondi il tuo IP + inbox aperta + identità dedicata. Per chi vuole restare davvero nascosto.</p>
        <button onClick={() => navigate('/tuono')} className="mt-2 border border-amber text-amber hover:bg-amber hover:text-black rounded-lg px-3 py-2 text-[13px]">⚡ Apri →</button>
      </div>

      {/* ── Opzioni avanzate (collassate: controllo fine, non obbligatorio) ── */}
      <Section icon="⚙" title="Opzioni avanzate" subtitle="solo per chi le vuole">

      {/* Nuke 5-PIN (toggle inline) */}
      <Opt
        title="☢ Nuke 5-PIN"
        state={nuke ? 'ATTIVO' : 'spento'}
        stateColor={nuke ? 'text-red-400' : 'text-ink-dim'}
        what="5 PIN sbagliati di fila cancellano tutto su questo telefono (recuperi con le 24 parole)."
        useful="temi che qualcuno ti costringa a sbloccare il telefono."
        cost="rischi di cancellare tutto sbagliando il PIN. Tieni le 24 parole al sicuro."
      >
        <button
          onClick={toggleNuke}
          className={`mt-1 border px-3 py-2 text-[14px] ${nuke ? 'border-red-500 text-red-400' : 'border-ink-dim text-ink hover:border-ink'}`}
        >
          {nuke ? '☢ Disattiva' : '☢ Attiva'}
        </button>
      </Opt>

      {/* Rileggi i miei messaggi / deniability (toggle inline) */}
      <Opt
        title="👁 Rileggi i miei messaggi"
        state={selfRead ? 'ON' : 'OFF (deniability)'}
        stateColor={selfRead ? 'text-ink' : 'text-amber'}
        what="se attivo, tieni una copia dei messaggi che invii, leggibile solo da te."
        useful="vuoi rivedere cosa hai scritto (lavoro, accordi, memoria)."
        cost="lascia una traccia sul telefono. Spento = nessuno, nemmeno tu, può rileggerli."
      >
        <button
          onClick={toggleSelfRead}
          className="mt-1 border border-ink-dim text-ink hover:border-ink px-3 py-2 text-[14px]"
        >
          {selfRead ? 'Disattiva (più deniability)' : 'Riattiva rilettura'}
        </button>
      </Opt>

      {/* Modalità identità (naviga) */}
      <Opt
        title="🎭 Modalità identità"
        state={idMode === 'reserved' ? 'Riservata' : idMode === 'hybrid' ? 'Ibrida' : 'Aperta'}
        stateColor={idMode === 'open' ? 'text-ink-dim' : 'text-ink'}
        what="Aperta = ti trovano per nome; Riservata = solo su invito; Ibrida = trovabile + più identità (Premium)."
        useful="vuoi sparire dalla ricerca (Riservata) o gestire più identità (Ibrida)."
        cost="in Riservata ti raggiungono solo su invito: meno comodo per farti trovare."
      >
        <button
          onClick={() => navigate('/premium')}
          className="mt-1 border border-ink-dim text-ink hover:border-ink px-3 py-2 text-[14px]"
        >
          Gestisci modalità →
        </button>
      </Opt>

      {/* Notifiche push (status + rimando) */}
      <Opt
        title="🔔 Notifiche push"
        state={notifOn ? 'attive' : 'spente'}
        stateColor={notifOn ? 'text-ink' : 'text-ink-dim'}
        what="ti avvisano quando arriva un messaggio, senza mostrare testo né mittente."
        useful="usi Nullum tutti i giorni e vuoi saperlo subito."
        cost="lasciano una traccia sul server. Spente = meno tracce."
      >
        <button
          onClick={() => navigate('/impostazioni')}
          className="mt-1 border border-ink-dim text-ink hover:border-ink px-3 py-2 text-[14px]"
        >
          Regola in ⚙ Impostazioni →
        </button>
      </Opt>

      {/* TTL — informativo */}
      <Opt
        title="⏳ Durata dei messaggi (TTL)"
        state="per messaggio"
        what="scegli quanto vive un messaggio dopo la lettura: zero, 10s, 5m, 1h, 24h, ∞ (Premium)."
        useful="messaggi delicati → zero/10s; chat che vuoi rivedere → 1h/24h."
        cost="durate lunghe tengono i messaggi sul server più a lungo."
      />
      </Section>

      {/* ── CONSIGLIO PREMIUM ───────────────────────────────────── */}
      <p className="text-[15px] text-ink mt-4 mb-2">Ti conviene Premium?</p>

      <Box variant={pStatus === 'grace' ? 'warn' : premiumActive ? 'success' : 'warn'} className="text-[13px] mb-2">
        {pStatus === 'lifetime'
          ? '✓ Hai Premium a vita.'
          : pStatus === 'active'
          ? `✓ Premium attivo${pExpires ? ` — scade il ${new Date(pExpires).toLocaleDateString('it-IT')}` : ''}${pDays != null ? ` (tra ${pDays} gg)` : ''}.`
          : pStatus === 'grace'
          ? `⚠ Premium scaduto — periodo di grazia${pGrace != null ? ` (${pGrace} gg)` : ''}: sola lettura. Rinnova per riattivare la creazione.`
          : 'Premium non attivo. La tua identità principale funziona benissimo lo stesso.'}
      </Box>

      <Box className="text-[13px] text-ink-dim space-y-2 mb-2">
        <p className="text-ink">Cosa aggiunge:</p>
        <p><b className="text-ink">Identità separate</b> — amici, lavoro, attivismo, senza collegamenti tra loro.
          {' '}<span className="text-ink">✓ utile se</span> tieni i tuoi mondi separati o lavori con clienti.
          {' '}<span className="text-amber">✕ inutile se</span> ti basta una sola identità.
          {derivedCount > 0 ? ` (ne hai ${derivedCount}).` : ''}</p>
        <p><b className="text-ink">Modalità Ibrida</b> — trovabile e con più identità insieme.
          {' '}<span className="text-ink">✓ utile se</span> vuoi farti trovare ma anche avere identità di contesto.</p>
        <p><b className="text-ink">Cambio delle 24 parole</b> — rigeneri le chiavi tenendo lo stesso @sigil.
          {' '}<span className="text-ink">✓ utile se</span> temi che qualcuno abbia visto le vecchie parole.</p>
        <p><b className="text-ink">Durata lunga / ∞</b> — chat che durano nel tempo.
          {' '}<span className="text-amber">✕ meglio evitarla</span> per i messaggi più delicati.</p>
      </Box>

      <Box className="text-[13px] mb-3 space-y-1">
        <p><span className="text-ink">Ti conviene se</span> <span className="text-ink-dim">gestisci più identità, lavori con clienti o vuoi chat che durano.</span></p>
        <p><span className="text-amber">Non ti serve se</span> <span className="text-ink-dim">usi Nullum per scambi veloci con una sola identità: la base ti basta.</span></p>
      </Box>

      <Btn variant="primary" onClick={() => navigate('/premium')}>
        ⭐ Vai a Premium
      </Btn>

      <Btn variant="ghost" className="mt-2" onClick={() => navigate('/info')}>
        📖 Manuale completo e privacy
      </Btn>

      <Footer>
        <Btn variant="ghost" onClick={() => navigate('/inbox')}>◀ Inbox</Btn>
      </Footer>
    </Frame>
  );
}
